/* eslint-disable react-hooks/rules-of-hooks */
import { MailOutlined } from '@ant-design/icons';
import { Space, Switch, Table, Tooltip } from 'antd';
import { putApi } from 'apis';
import { PermissionContext } from 'components/Global/AuthLayout';
import { FC, useContext } from 'react';
import { DeleteActionSvg, EditActionSvg } from 'utils/svgs';
import { checkPermission, toastText } from 'utils/utils';
import SearchAndFilter from '../SearchAndFilter';
import styles from './index.module.scss';
import './index.scss';
import { DynamicTableProps } from './types';

const UserTable: FC<DynamicTableProps> = (props) => {
	const { Column } = Table;

	const {
		userDataSource,
		performSearchHandler,
		performFilterHandler,
		searchValue,
		filterValue,
		showModal,
		openDrawerHandler,
		setDrawerInfoHandler,
		setEditSelectedUser,
		totalRecords,
		currentPage,
		PageSize,
		paginationChangeHandler,
		isLoading,
		reinviteUserAction,
	} = props;

	const context = useContext(PermissionContext);

	const isEditPermission = checkPermission(context.permissions, {
		permissionName: 'Users',
		permission: ['edit'],
	});

	const isDeletePermission = checkPermission(context.permissions, {
		permissionName: 'Users',
		permission: ['delete'],
	});

	const editDataHandler = (userObject: any) => {
		openDrawerHandler();
		setDrawerInfoHandler('Edit User');
		setEditSelectedUser(userObject);
	};

	// Delete user data handler
	const deleteDataHandler = (userObject: any) => {
		setEditSelectedUser(userObject);
		showModal();
	};

	// Handle user status change
	const statusHandler = async (value: any, data: any) => {
		const finalData: any = {
			updateUserCompanyRoleId: data?.userCompanyRoleId,
			status: value,
		};

		try {
			await putApi('/user/status-update', finalData);
			toastText('Status updated successfully', 'success');
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				`Something went wrong in status update`;
			toastText(message, 'error');
		}
	};

	return (
		<div className={styles['dynamic-table']}>
			<SearchAndFilter
				performSearchHandler={performSearchHandler}
				searchValue={searchValue}
				performFilterHandler={performFilterHandler}
				filterValue={filterValue}
				PageSize={PageSize}
			/>
			<Table
				dataSource={userDataSource}
				pagination={{
					total: totalRecords,
					current: currentPage,
					onChange: paginationChangeHandler,
					pageSize: PageSize,
					showSizeChanger: false,
				}}
				scroll={{ y: 'calc(100vh - 400px)' }}
				loading={isLoading}
			>
				<Column
					title="Name"
					dataIndex="name"
					key="name"
					className="bg-white"
					render={(value: any) => value || '-'}
				/>
				<Column
					title="Role"
					dataIndex="roleName"
					key="roleName"
					className="bg-white"
					render={(value: any) => value || '-'}
				/>
				<Column
					title="Email"
					dataIndex="email"
					key="email"
					className="bg-white"
					render={(value: any) => value || '-'}
				/>
				<Column
					title="Status"
					dataIndex="status"
					key="status"
					className="bg-white"
					render={(value: any, data: any) => {
						return (
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
								}}
							>
								<Space>
									<Switch
										key={data.userId}
										defaultChecked={value}
										onChange={(e) => statusHandler(e, data)}
										disabled={
											!isEditPermission ||
											data.userId === context.userId ||
											!data.isVerified ||
											(data.isAdmin &&
												!context.isSuperAdmin) ||
											data.isTimeLogExist ||
											data.isMappingExist
										}
									/>
									{(data.isTimeLogExist ||
										data.isMappingExist) && (
										<Tooltip
											title={
												data.isTimeLogExist
													? 'Time log already exists for this supervisor'
													: 'Employees are already assigned to this supervisor'
											}
										>
											<span className="ml-2 text-red-500 cursor-not-allowed">
												!
											</span>
										</Tooltip>
									)}
								</Space>
							</div>
						);
					}}
				/>
				{(isEditPermission || isDeletePermission) && (
					<Column
						title="Action"
						dataIndex="action"
						key="action"
						className="bg-white"
						width={'15%'}
						render={(value, data: any) => {
							return (
								<Space size={20}>
									{isEditPermission &&
									!(
										data.isAdmin &&
										context.isSuperAdmin === false
									) &&
									data.userId !== context.userId ? (
										<div
											className="cursor-pointer flex align-center justify-center"
											onClick={() =>
												editDataHandler(data)
											}
										>
											<EditActionSvg />
										</div>
									) : (
										<div className=" cursor-not-allowed flex align-center justify-center">
											<EditActionSvg color="#928F8F" />
										</div>
									)}

									{isDeletePermission &&
									!(
										data.isAdmin &&
										context.isSuperAdmin === false
									) &&
									data.userId !== context.userId &&
									!data.isTimeLogExist &&
									!data.isMappingExist ? (
										<div
											className="cursor-pointer flex align-center justify-center"
											onClick={() =>
												deleteDataHandler(data)
											}
										>
											<DeleteActionSvg />
										</div>
									) : (
										<Tooltip
											title={
												data.isTimeLogExist
													? 'Time log already exists for this supervisor'
													: data.isMappingExist
													? 'Employees are already assigned to this supervisor'
													: 'Delete not allowed'
											}
										>
											<div className="cursor-not-allowed flex align-center justify-center">
												<DeleteActionSvg color="#928F8F" />
											</div>
										</Tooltip>
									)}

									{data.invitationStatus === 'Pending' && (
										<Tooltip title="Reinvite">
											<div
												className="mail-icon"
												onClick={() => {
													reinviteUserAction(data);
												}}
											>
												<MailOutlined />
											</div>
										</Tooltip>
									)}
								</Space>
							);
						}}
					/>
				)}
			</Table>
		</div>
	);
};

export default UserTable;
